// Hooks do domínio de CHAMADAS (telefonia) e dos relatórios que saem direto
// delas: a listagem, o resumo agregado, a taxa de conversão do CRM e o sucesso
// por tentativa. Mesmo padrão de use-brands.ts:
//   - `src/api/calls.ts` guarda o request (fetch + tipos), sem React;
//   - este arquivo guarda as CHAVES e os hooks do TanStack Query;
//   - a tela não importa a função de api: importa o hook.
//
// As chaves abaixo são as que já estavam espalhadas pelas telas, string por
// string — centralizar não é renomear. O React Query invalida por PREFIXO, então
// uma chave que divergisse uma letra viraria dado velho na tela sem erro nenhum.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deleteCallRecording,
  getCall,
  getCallRecordingUrl,
  getCalls,
  getCallsSummary,
  getCallTranscription,
  getConversionsByAttempt,
  getConversionStageOptions,
  getConversionStages,
  getConversionsSummary,
  isTerminalStatus,
  putConversionStages,
  type CallsQuery,
  type CallSummaryQuery,
  type ConversionsSummaryQuery,
  type ConversionStageResult,
  type ConversionStagesResponse,
} from "@/api/calls";
// As duas chaves de dinheiro que o apagar-gravação derruba. Vêm dos hooks dos
// domínios que agora existem (armazenamento de gravação entra na conta do mês);
// os valores e a ordem das invalidações são os mesmos de antes.
import { costKeys } from "./use-costs.js";
import { planKeys } from "./use-plans.js";

// Recorte de período já em ISO, do jeito que as telas de relatório montam.
// `null` = o usuário limpou o período; a chave carrega os dois campos como
// `undefined`, exatamente como estava inline nas telas.
export interface IsoRange {
  from: string;
  to: string;
}

// Ligações "ao vivo": as duas telas que mostram o painel usam este MESMO
// recorte, então ele vive aqui — chave igual com query diferente seria cache
// compartilhado servindo dado de outro recorte.
const ACTIVE_CALLS_QUERY: CallsQuery = { active: true, limit: 100 };

// Player e transcrição do detalhe: a URL é assinada e curta, mas reabrir o
// mesmo diálogo em menos de um minuto não precisa de outra assinatura.
const RECORDING_STALE_MS = 60_000;

// As chaves DESTE domínio. Ressalvas herdadas que valem leitura:
//
//  - `all` (["calls"]) é PREFIXO de TODAS as outras de listagem/resumo —
//    inclusive das de relatório. É de propósito: o SSE de eventos de chamada e
//    a exclusão de gravação invalidam ["calls"] e derrubam o conjunto inteiro.
//    NÃO é prefixo de `detail`/`recording`/`transcription` (["call", …],
//    ["call-recording", …]) — essas ficam de fora e são invalidadas à parte.
//  - o sufixo de escopo ("dashboard", "home", "sellers", …) existe para telas
//    diferentes NÃO dividirem a mesma entrada de cache do mesmo endpoint. Cada
//    uma manda um recorte próprio (só-discador, período, janela da hora); uma
//    chave só faria a última a buscar sobrescrever o número da outra.
//  - `reportSummary` foge da forma das irmãs: é ["calls","report","summary",…]
//    e não ["calls","summary","report",…]. Divergência pré-existente da tela de
//    Relatório de Ligações, preservada — trocar a chave aqui não quebraria
//    teste nenhum, só faria a tela mostrar dado velho.
//  - o painel de auditoria do click-to-call chama os MESMOS endpoints de
//    gravação/transcrição sob ["click-to-call-audit-recording", id] e
//    ["click-to-call-audit-transcription", id] (ver click-to-call-audit-screen).
//    São duas entradas de cache para o mesmo dado; divergência pré-existente,
//    preservada — unificar é mudança de comportamento, fica para outra vez.
export const callKeys = {
  all: ["calls"] as const,
  active: ["calls", "active"] as const,
  report: (query: CallsQuery) => ["calls", "report", query] as const,
  reportSummary: (filters: CallSummaryQuery) => ["calls", "report", "summary", filters] as const,
  unified: (query: CallsQuery) => ["calls", "unified", query] as const,
  summaryToday: (from: string) => ["calls", "summary", "today", from] as const,
  summaryPace: (hourBucket: string) => ["calls", "summary", "pace", hourBucket] as const,
  summaryHome: (from: string, to: string) => ["calls", "summary", "home", from, to] as const,
  summaryDashboard: (from?: string, to?: string) =>
    ["calls", "summary", "dashboard", from, to] as const,
  summaryHealth: (from?: string, to?: string) => ["calls", "summary", "health", from, to] as const,
  summarySellers: (from?: string, to?: string) =>
    ["calls", "summary", "sellers", from, to] as const,
  summaryUnified: (filters: CallSummaryQuery) => ["calls", "summary", "unified", filters] as const,
  detail: (callId: string | null | undefined) => ["call", callId] as const,
  recording: (callId: string | undefined) => ["call-recording", callId] as const,
  transcription: (callId: string | undefined) => ["call-transcription", callId] as const,
};

// Conversão do CRM: outra raiz de cache (["conversions"], ["conversion-stages"]),
// de propósito — invalidar ["calls"] não derruba a taxa de conversão, que vem de
// outro endpoint e de outra fonte (a virada de etapa no CRM, não a ligação).
export const conversionKeys = {
  summaryDashboard: (from?: string, to?: string) =>
    ["conversions", "summary", "dashboard", from, to] as const,
  summarySellers: (from?: string, to?: string) =>
    ["conversions", "summary", "sellers", from, to] as const,
  summaryUnified: (filters: ConversionsSummaryQuery) =>
    ["conversions", "summary", "unified", filters] as const,
  attempts: (from?: string, to?: string) => ["conversions", "attempts", from, to] as const,
  stages: ["conversion-stages"] as const,
  stageOptions: ["conversion-stage-options"] as const,
};

// --- Listagem de chamadas ---

// Ligações em curso (RINGING + IN_PROGRESS). Três telas usam esta MESMA entrada
// de cache; o que muda entre elas é só o ritmo do poll e o gate de organização.
export function useActiveCalls(
  options: { enabled?: boolean; refetchInterval?: number; refetchOnWindowFocus?: boolean } = {},
) {
  return useQuery({
    queryKey: callKeys.active,
    queryFn: () => getCalls(ACTIVE_CALLS_QUERY),
    enabled: options.enabled,
    refetchInterval: options.refetchInterval,
    refetchOnWindowFocus: options.refetchOnWindowFocus,
  });
}

// Relatório de Ligações (tabela paginada). `placeholderData` mantém a página
// anterior na tela enquanto a nova chega — sem ela a tabela pisca vazia.
export function useCallsReport(query: CallsQuery) {
  return useQuery({
    queryKey: callKeys.report(query),
    queryFn: () => getCalls(query),
    placeholderData: (prev) => prev,
  });
}

// Agregados do MESMO recorte da tabela acima, em consulta separada: cobrem
// todas as ligações do período, não só as exibidas na página.
export function useCallsReportSummary(filters: CallSummaryQuery) {
  return useQuery({
    queryKey: callKeys.reportSummary(filters),
    queryFn: () => getCallsSummary(filters),
    placeholderData: (prev) => prev,
  });
}

// Visão unificada de Relatórios (a tabela).
export function useUnifiedCalls(query: CallsQuery) {
  return useQuery({
    queryKey: callKeys.unified(query),
    queryFn: () => getCalls(query),
    placeholderData: (previous) => previous,
  });
}

// --- Resumo agregado (GET /v1/calls/summary) ---

// Um hook por tela em vez de um genérico com a chave no parâmetro: é o sufixo
// de escopo que separa as entradas de cache, e ele precisa morar junto da
// chave para continuar auditável.

export function useUnifiedCallsSummary(filters: CallSummaryQuery) {
  return useQuery({
    queryKey: callKeys.summaryUnified(filters),
    queryFn: () => getCallsSummary(filters),
  });
}

// Ligações do dia na tela de Chamadas ativas. A chave leva só o `from`: o
// recorte é sempre o dia inteiro, então o `to` não distingue nada.
export function useTodayCallsSummary(day: IsoRange, options: { refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: callKeys.summaryToday(day.from),
    queryFn: () => getCallsSummary(day),
    refetchInterval: options.refetchInterval,
  });
}

// "Ritmo de hoje" do Dashboard. A janela é recalculada NA HORA do fetch (daí a
// função em `window`, e não um objeto): a chave só troca de hora em hora, mas
// cada refetch precisa enxergar até o minuto atual.
export function usePaceCallsSummary(
  hourBucket: string,
  window: () => CallSummaryQuery,
  options: { refetchInterval?: number } = {},
) {
  return useQuery({
    queryKey: callKeys.summaryPace(hourBucket),
    queryFn: () => getCallsSummary(window()),
    refetchInterval: options.refetchInterval,
  });
}

export function useHomeCallsSummary(range: IsoRange, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: callKeys.summaryHome(range.from, range.to),
    queryFn: () => getCallsSummary(range),
    enabled: options.enabled,
  });
}

// Dashboard: só o período, sem recorte de origem — o painel fala da operação
// inteira, click-to-call incluído.
export function useDashboardCallsSummary(
  range: IsoRange | null,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: callKeys.summaryDashboard(range?.from, range?.to),
    queryFn: () => getCallsSummary(range ? { from: range.from, to: range.to } : {}),
    enabled: options.enabled,
  });
}

// Saúde das ligações e Relatório de Vendedores mandam um recorte fixo junto do
// período (`params`, hoje `{ predictive: true }`). Ele fica na TELA, que é onde
// o comentário explica por que aquele universo de ligações e não outro; aqui só
// se junta ao período, na mesma ordem de antes (`{ ...params, ...range }`).
export function useCallHealthSummary(
  range: IsoRange | null,
  params: CallSummaryQuery,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: callKeys.summaryHealth(range?.from, range?.to),
    queryFn: () => getCallsSummary(range ? { ...params, ...range } : { ...params }),
    enabled: options.enabled,
  });
}

export function useSellersCallsSummary(
  range: IsoRange | null,
  params: CallSummaryQuery,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: callKeys.summarySellers(range?.from, range?.to),
    queryFn: () => getCallsSummary(range ? { ...params, ...range } : { ...params }),
    enabled: options.enabled,
  });
}

// --- Taxa de conversão do CRM ---

export function useDashboardConversionsSummary(
  range: IsoRange | null,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: conversionKeys.summaryDashboard(range?.from, range?.to),
    queryFn: () => getConversionsSummary(range ? { from: range.from, to: range.to } : {}),
    enabled: options.enabled,
  });
}

export function useUnifiedConversionsSummary(filters: ConversionsSummaryQuery) {
  return useQuery({
    queryKey: conversionKeys.summaryUnified(filters),
    queryFn: () => getConversionsSummary(filters),
  });
}

export function useSellersConversionsSummary(
  range: IsoRange | null,
  params: ConversionsSummaryQuery,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: conversionKeys.summarySellers(range?.from, range?.to),
    queryFn: () => getConversionsSummary(range ? { ...params, ...range } : { ...params }),
    enabled: options.enabled,
  });
}

// Sucesso por tentativa: outra granularidade (balde de tentativa, não vendedor)
// e outra vida útil — por isso consulta própria, não um campo do resumo.
export function useConversionsByAttempt(
  range: IsoRange | null,
  params: ConversionsSummaryQuery,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: conversionKeys.attempts(range?.from, range?.to),
    queryFn: () => getConversionsByAttempt(range ? { ...params, ...range } : { ...params }),
    enabled: options.enabled,
  });
}

// --- Etapas de conversão (a régua que decide o que conta como conversão) ---

// GET /v1/conversion-stages é aberto a qualquer membro da org; quem pode
// devolver 403 por papel é o /options abaixo (requireAdmin no back).
export function useConversionStages(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: conversionKeys.stages,
    queryFn: () => getConversionStages(),
    enabled: options.enabled,
  });
}

export function useConversionStageOptions() {
  return useQuery({
    queryKey: conversionKeys.stageOptions,
    queryFn: () => getConversionStageOptions(),
  });
}

// Salvar o mapeamento invalida ["conversion-stages"] para TODO chamador (o badge
// de etapa da listagem de ligações lê a mesma chave), então a invalidação é do
// hook. O resto do que a tela faz no sucesso (semear o rascunho, avisar) continua
// dela: por isso os handlers entram como parâmetro, e não via
// `mutate(vars, { onSuccess })` — assim a ORDEM original é preservada (tela
// primeiro, invalidação depois), em vez de o toast esperar o refetch.
export function useSaveConversionStages(
  handlers: {
    onSuccess?: (data: ConversionStagesResponse) => void;
    onError?: (error: Error) => void;
  } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    // Seta em vez de passar `putConversionStages` direto: o TanStack chama o
    // mutationFn com (variáveis, contexto), e um segundo parâmetro opcional
    // receberia o contexto da mutação como se fosse dado.
    mutationFn: (stages: { stage: string; result: ConversionStageResult }[]) =>
      putConversionStages(stages),
    onSuccess: (data) => {
      handlers.onSuccess?.(data);
      void queryClient.invalidateQueries({ queryKey: conversionKeys.stages });
    },
    onError: handlers.onError,
  });
}

// --- Uma chamada ---

// Acompanha a chamada em curso e PARA de repetir quando ela chega a um status
// terminal — daí o refetchInterval ser função do dado, não um número fixo.
export function useCall(
  callId: string | null,
  options: { enabled?: boolean; pollMs?: number } = {},
) {
  const pollMs = options.pollMs ?? 2000;
  return useQuery({
    queryKey: callKeys.detail(callId),
    queryFn: () => getCall(callId as string),
    enabled: options.enabled ?? !!callId,
    refetchInterval: (query) => (isTerminalStatus(query.state.data?.status) ? false : pollMs),
  });
}

// URL assinada da gravação. Quem chama controla o `enabled` (só quando o
// diálogo abre e a ligação tem gravação) — daí o `!` no queryFn.
export function useCallRecording(callId: string | undefined, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: callKeys.recording(callId),
    queryFn: () => getCallRecordingUrl(callId!),
    enabled: options.enabled,
    staleTime: RECORDING_STALE_MS,
  });
}

// Transcrição diarizada. Consulta à parte da listagem para não arrastar texto
// longo em cada linha da tabela.
export function useCallTranscription(
  callId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: callKeys.transcription(callId),
    queryFn: () => getCallTranscription(callId!),
    enabled: options.enabled,
    staleTime: RECORDING_STALE_MS,
  });
}

// Baixar a gravação é uma AÇÃO, não uma consulta: pede a URL assinada na hora do
// clique (a do player pode estar expirada) e entrega ao chamador, que monta o
// link. Sem efeito de cache — nada mudou no servidor.
export function useCallRecordingDownload(
  callId: string,
  handlers: {
    onSuccess?: (urls: { url: string; downloadUrl?: string }) => void;
    onError?: (error: Error) => void;
  } = {},
) {
  return useMutation({
    mutationFn: () => getCallRecordingUrl(callId),
    onSuccess: handlers.onSuccess,
    onError: handlers.onError,
  });
}

// Apagar a gravação: some o WAV, a ligação e a transcrição continuam. A
// invalidação é a mesma para todo chamador, então é do hook — inclusive
// `costKeys.billing` (["billing"]) e `planKeys.subscription` (["subscription"]),
// que são de outros domínios: armazenamento de gravação entra na conta do mês.
// Eram literais aqui enquanto o domínio financeiro não tinha saído de api.ts;
// agora apontam para a fonte única, com os mesmos valores e na mesma ordem.
export function useDeleteCallRecording(
  callId: string,
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteCallRecording(callId),
    onSuccess: async () => {
      handlers.onSuccess?.();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: callKeys.all }),
        queryClient.invalidateQueries({ queryKey: callKeys.recording(callId) }),
        queryClient.invalidateQueries({ queryKey: costKeys.billing }),
        queryClient.invalidateQueries({ queryKey: planKeys.subscription }),
      ]);
    },
    onError: handlers.onError,
  });
}
